// İSG uzmanı portalı — Kontrol İtemları kataloğu (Ayarlar; api-contract §25, tenant-scoped). Firma
// kendi kontrol itemı kataloğunu yönetir: genel (bizim) varsayılan checklist ("Genel" rozeti) + firma
// özel öge. Liste (UTable sözleşmesi: page/pageSize/search + total/filteredTotal) + aktif filtresi,
// oluştur/düzenle (ControlItemFormModal), **pasifleştir/sil** (soft, onay). Genel öge de
// düzenlenebilir/pasifleştirilebilir (`isDefault` backend'de korunur — §25.1). Kontrol itemları
// permit-tipine (§25.2) bağlanır; izinde İSG uzmanı işaretler (§25.3). Auth gate = `permit-types` (D3).
//
// KKD kataloğunun (ProtectiveEquipmentListPage) muadili; tek fark alan adı (`text` vs `name`) + kanon §25.

import {
  Alert,
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  type ControlItemDto,
  EmptyState,
  Pagination,
  RowActionsMenu,
  SelectMenu,
  type SelectOption,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  TableToolbarFilter,
  Tag,
  controlItemsApi,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ListChecks, Plus, SearchX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShellHeaderAction } from '../../components/layout/ShellHeaderAction';
import { ControlItemFormModal } from '../../components/permit-types/ControlItemFormModal';
import { controlItemsKeys } from '../../lib/controlItemsKeys';
import { useTableDensity } from '../../lib/uiPrefs';
import { useDebouncedValue } from '../../lib/useDebouncedValue';

const PAGE_SIZE = 20;

/** Aktif filtresi string değeri → `isActive` param (undefined = hepsi). */
function activeParam(value: string): boolean | undefined {
  if (value === 'active') {
    return true;
  }
  if (value === 'inactive') {
    return false;
  }
  return undefined;
}

export function ControlItemsListPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<ControlItemDto | null>(null);
  const [deleteItem, setDeleteItem] = useState<ControlItemDto | null>(null);

  function resetPage<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  const statusOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: t('permitTypes.list.filter.allStatuses') },
      { value: 'active', label: t('permitTypes.list.filter.active') },
      { value: 'inactive', label: t('permitTypes.list.filter.inactive') },
    ],
    [t],
  );

  const params = {
    isActive: activeParam(statusFilter),
    search: debouncedSearch,
    page,
    pageSize: PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: controlItemsKeys.listParams(params),
    queryFn: () => controlItemsApi.listControlItems(params),
    placeholderData: (prev) => prev,
  });

  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => controlItemsApi.deleteControlItem(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: controlItemsKeys.list() });
      queryClient.invalidateQueries({ queryKey: controlItemsKeys.options() });
      setMessage(t('permitTypes.catalog.control.deleted'));
      setDeleteItem(null);
    },
  });

  const rows = query.data?.items ?? [];
  const [density, setDensity] = useTableDensity();
  const hasActiveFilters = Boolean(debouncedSearch || statusFilter);
  function clearFilters() {
    setSearch('');
    setStatusFilter('');
    setPage(1);
  }

  return (
    <div className="flex flex-col gap-5">
      <ShellHeaderAction>
        <Button
          onClick={() => {
            setMessage(null);
            setCreateOpen(true);
          }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('permitTypes.catalog.control.new')}
        </Button>
      </ShellHeaderAction>

      {message ? <Alert tone="success">{message}</Alert> : null}

      <Card className="carbon:p-0">
        <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
          <TableToolbar
            summary={
              query.data
                ? t('permitTypes.catalog.control.count', { count: query.data.filteredTotal })
                : undefined
            }
            search={search}
            onSearchChange={resetPage(setSearch)}
            searchLabel={t('permitTypes.catalog.control.search')}
            searchPlaceholder={t('permitTypes.catalog.control.searchPlaceholder')}
            density={density}
            onDensityChange={setDensity}
            densityLabel={t('common.compactRows')}
            filters={
              <TableToolbarFilter>
                <SelectMenu
                  ariaLabel={t('permitTypes.list.filter.status')}
                  placeholder={t('permitTypes.list.filter.allStatuses')}
                  options={statusOptions}
                  value={statusFilter}
                  onValueChange={resetPage(setStatusFilter)}
                />
              </TableToolbarFilter>
            }
          />

          <Table caption={t('permitTypes.catalog.control.title')} density={density}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('permitTypes.catalog.control.col.text')}</TableHead>
                <TableHead>{t('permitTypes.catalog.col.origin')}</TableHead>
                <TableHead>{t('permitTypes.list.col.status')}</TableHead>
                <TableHead className="w-12">
                  <span className="sr-only">{t('permitTypes.common.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody aria-busy={query.isPending || undefined}>
              {query.isPending ? (
                <TableSkeletonRows columns={4} />
              ) : query.isError ? (
                <TableMessageRow colSpan={4}>
                  {resolveApiErrorMessage(query.error, t)}
                </TableMessageRow>
              ) : rows.length === 0 ? (
                <TableMessageRow colSpan={4}>
                  {hasActiveFilters ? (
                    <EmptyState
                      icon={SearchX}
                      title={t('permitTypes.catalog.control.noResults')}
                      description={t('common.noResults.body')}
                      action={
                        <Button variant="ghost" size="sm" onClick={clearFilters}>
                          {t('common.clearFilters')}
                        </Button>
                      }
                      role="status"
                    />
                  ) : (
                    <EmptyState
                      icon={ListChecks}
                      title={t('permitTypes.catalog.control.empty')}
                      description={t('permitTypes.catalog.control.empty.body')}
                      action={
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setMessage(null);
                            setCreateOpen(true);
                          }}
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          {t('permitTypes.catalog.control.new')}
                        </Button>
                      }
                    />
                  )}
                </TableMessageRow>
              ) : (
                rows.map((item) => (
                  <TableRow key={item.uuid}>
                    <TableCell>
                      {/* Birincil "aç" = ad hücresi (S33 Slice 4 QA fix, defekt #4) — bu katalog
                          ögesinin ayrı bir görünüm rotası yok, "açmak" düzenleme modalını açmakla
                          AYNI (Personel panelinin Sheet açan düğmesiyle aynı desen — bağlantı
                          DEĞİL, `<button>`). */}
                      <button
                        type="button"
                        onClick={() => {
                          setMessage(null);
                          setEditItem(item);
                        }}
                        className="block text-left font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {item.text}
                      </button>
                      {item.description ? (
                        <span className="block text-xs text-muted">{item.description}</span>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Tag color={item.isDefault ? 'blue' : 'gray'}>
                        {item.isDefault
                          ? t('permitTypes.catalog.origin.default')
                          : t('permitTypes.catalog.origin.custom')}
                      </Tag>
                    </TableCell>
                    <TableCell>
                      <Tag color={item.isActive ? 'green' : 'gray'}>
                        {item.isActive
                          ? t('permitTypes.status.active')
                          : t('permitTypes.status.inactive')}
                      </Tag>
                    </TableCell>
                    <TableCell className="w-12 px-2">
                      <RowActionsMenu
                        ariaLabel={t('permitTypes.list.action.openMenu', { name: item.text })}
                        items={[
                          {
                            key: 'delete',
                            label: t('permitTypes.list.action.delete'),
                            destructive: true,
                            onSelect: () => {
                              setMessage(null);
                              setDeleteItem(item);
                            },
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {query.data ? (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              filteredTotal={query.data.filteredTotal}
              onPageChange={setPage}
              labels={{
                nav: t('permitTypes.pagination.nav'),
                previous: t('permitTypes.pagination.previous'),
                next: t('permitTypes.pagination.next'),
              }}
            />
          ) : null}
        </CardContent>
      </Card>

      <ControlItemFormModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSuccess={(msg) => setMessage(msg)}
      />
      <ControlItemFormModal
        open={editItem !== null}
        onOpenChange={(open) => !open && setEditItem(null)}
        item={editItem}
        onSuccess={(msg) => setMessage(msg)}
      />
      <ConfirmDialog
        open={deleteItem !== null}
        onOpenChange={(open) => !open && setDeleteItem(null)}
        title={t('permitTypes.catalog.control.delete.title')}
        body={t('permitTypes.catalog.control.delete.body', { text: deleteItem?.text ?? '' })}
        confirmLabel={t('permitTypes.catalog.delete.confirm')}
        loading={deleteMutation.isPending}
        error={deleteMutation.isError ? deleteMutation.error : undefined}
        onConfirm={() => deleteItem && deleteMutation.mutate(deleteItem.uuid)}
      />
    </div>
  );
}
